import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Grid,
  Checkbox,
} from '@mui/material';
import { Add, Campaign } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import announcementService, { CreateAnnouncementRequest, Announcement } from '../../services/announcementService';
import storeService from '../../services/storeService';
import clusterService from '../../services/clusterService';
import userService from '../../services/userService';
import { USER_ROLE_OPTIONS } from '../../constants';
import { Store, Cluster, User } from '../../types';

const announcementSchema = yup.object({
  title: yup.string().required('Title is required').max(200, 'Title must not exceed 200 characters'),
  message: yup.string().required('Message is required'),
  priority: yup.string().oneOf(['low', 'medium', 'high', 'urgent']).default('medium'),
  expires_at: yup.string().nullable().optional(),
  target_type: yup.string().oneOf(['all', 'role', 'store', 'cluster', 'user']).required('Target type is required'),
  target_roles: yup.array().of(yup.string()).when('target_type', {
    is: 'role',
    then: (schema) => schema.min(1, 'At least one role must be selected').required(),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  target_stores: yup.array().of(yup.string()).when('target_type', {
    is: 'store',
    then: (schema) => schema.min(1, 'At least one store must be selected').required(),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  target_clusters: yup.array().of(yup.string()).when('target_type', {
    is: 'cluster',
    then: (schema) => schema.min(1, 'At least one cluster must be selected').required(),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  target_users: yup.array().of(yup.string()).when('target_type', {
    is: 'user',
    then: (schema) => schema.min(1, 'At least one user must be selected').required(),
    otherwise: (schema) => schema.optional().nullable(),
  }),
});

const AnnouncementsPage: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stores, setStores] = useState<Store[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  type AnnouncementFormData = {
    title: string;
    message: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    expires_at: string | null;
    target_type: 'all' | 'role' | 'store' | 'cluster' | 'user';
    target_roles?: string[];
    target_stores?: string[];
    target_clusters?: string[];
    target_users?: string[];
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<AnnouncementFormData>({
    resolver: yupResolver(announcementSchema) as any,
    defaultValues: {
      priority: 'medium',
      target_type: 'all',
      title: '',
      message: '',
      expires_at: null,
    },
  });

  const targetType = watch('target_type');

  useEffect(() => {
    fetchStores();
    fetchClusters();
    fetchUsers();
    loadAnnouncements();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await storeService.getStores({ limit: 1000 });
      setStores(response.stores || []);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchClusters = async () => {
    try {
      const response = await clusterService.getClusters();
      setClusters(response.clusters || []);
    } catch (error) {
      console.error('Error fetching clusters:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await userService.getUsers({ limit: 1000 });
      setUsers(response.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const loadAnnouncements = async () => {
    try {
      setHistoryLoading(true);
      const data = await announcementService.getAnnouncements();
      setAnnouncements(data);
    } catch (err: any) {
      console.error('Error fetching announcements:', err);
      // Don't surface as main error; history is secondary
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    reset({
      priority: 'medium',
      target_type: 'all',
    });
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
    setError('');
    setSuccess('');
  };

  const onSubmit = async (data: AnnouncementFormData) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Prepare the request
      const requestData: CreateAnnouncementRequest = {
        title: data.title,
        message: data.message,
        priority: data.priority || 'medium',
        expires_at: data.expires_at ? new Date(data.expires_at).toISOString() : null,
        target_type: data.target_type,
      };

      // Add target-specific data
      if (data.target_type === 'role' && data.target_roles) {
        requestData.target_roles = data.target_roles;
      } else if (data.target_type === 'store' && data.target_stores) {
        requestData.target_stores = data.target_stores;
      } else if (data.target_type === 'cluster' && data.target_clusters) {
        requestData.target_clusters = data.target_clusters;
      } else if (data.target_type === 'user' && data.target_users) {
        requestData.target_users = data.target_users;
      }

      const result = await announcementService.createAnnouncement(requestData);
      setSuccess(`Announcement created successfully! Sent to ${result.created} user(s).`);
      // Reload announcement history so the new one appears
      loadAnnouncements();
      setTimeout(() => {
        handleCloseDialog();
      }, 2000);
    } catch (err: any) {
      console.error('Error creating announcement:', err);
      setError(err.response?.data?.message || 'Failed to create announcement');
    } finally {
      setLoading(false);
    }
  };

  return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Campaign sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Announcements
          </Typography>
        </Box>

        {/* Create announcement box */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Create and manage announcements that will be visible to users in the mobile app.
            You can target all users, specific roles, stores, clusters, or individual users.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenDialog}
          >
            Create Announcement
          </Button>
        </Paper>

        {/* Announcement history box */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Recent Announcements
          </Typography>
          {historyLoading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : announcements.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No announcements have been created yet.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 480, overflowY: 'auto', mt: 1 }}>
              {announcements.map((a) => (
                <Box
                  key={a.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    py: 1,
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <Box sx={{ mr: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {a.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {a.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.createdAt
                        ? new Date(a.createdAt).toLocaleString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '-'}
                      {a.createdByName
                        ? ` • Created by ${a.createdByName}${
                            a.createdByEmployeeId ? ` (${a.createdByEmployeeId})` : ''
                          }`
                        : a.createdBy
                        ? ` • Created by ${a.createdBy}`
                        : ''}
                    </Typography>
                  </Box>
                  <Chip
                    label={a.priority.toUpperCase()}
                    size="small"
                    color={
                      a.priority === 'urgent'
                        ? 'error'
                        : a.priority === 'high'
                        ? 'warning'
                        : a.priority === 'medium'
                        ? 'info'
                        : 'default'
                    }
                  />
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>Create New Announcement</DialogTitle>
            <DialogContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    {...register('title')}
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    multiline
                    rows={4}
                    {...register('message')}
                    error={!!errors.message}
                    helperText={errors.message?.message}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      {...register('priority')}
                      label="Priority"
                      defaultValue="medium"
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Expires At (Optional)"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    {...register('expires_at')}
                    error={!!errors.expires_at}
                    helperText={errors.expires_at?.message}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Target Audience</InputLabel>
                    <Controller
                      name="target_type"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Target Audience"
                        >
                          <MenuItem value="all">All Users</MenuItem>
                          <MenuItem value="role">By Role</MenuItem>
                          <MenuItem value="store">By Store</MenuItem>
                          <MenuItem value="cluster">By Cluster</MenuItem>
                          <MenuItem value="user">Specific Users</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>

                {targetType === 'role' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth error={!!(errors as any).target_roles}>
                      <InputLabel>Select Roles</InputLabel>
                      <Controller
                        name="target_roles"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            multiple
                            label="Select Roles"
                            value={field.value || []}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(selected as string[]).map((value) => (
                                  <Chip
                                    key={value}
                                    label={USER_ROLE_OPTIONS.find((opt) => opt.value === value)?.label || value}
                                    size="small"
                                  />
                                ))}
                              </Box>
                            )}
                          >
                            {USER_ROLE_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Checkbox checked={(field.value || []).includes(option.value)} />
                                <Box sx={{ ml: 1 }}>{option.label}</Box>
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {(errors as any).target_roles && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {(errors as any).target_roles.message}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                )}

                {targetType === 'store' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth error={!!(errors as any).target_stores}>
                      <InputLabel>Select Stores</InputLabel>
                      <Controller
                        name="target_stores"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            multiple
                            label="Select Stores"
                            value={field.value || []}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(selected as string[]).map((value) => {
                                  const store = stores.find((s) => s.id === value);
                                  return (
                                    <Chip
                                      key={value}
                                      label={store?.name || value}
                                      size="small"
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {stores.map((store) => (
                              <MenuItem key={store.id} value={store.id}>
                                <Checkbox checked={(field.value || []).includes(store.id)} />
                                {store.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {(errors as any).target_stores && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {(errors as any).target_stores.message}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                )}

                {targetType === 'cluster' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth error={!!(errors as any).target_clusters}>
                      <InputLabel>Select Clusters</InputLabel>
                      <Controller
                        name="target_clusters"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            multiple
                            label="Select Clusters"
                            value={field.value || []}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(selected as string[]).map((value) => {
                                  const cluster = clusters.find((c) => c.id === value);
                                  return (
                                    <Chip
                                      key={value}
                                      label={cluster?.name || value}
                                      size="small"
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {clusters.map((cluster) => (
                              <MenuItem key={cluster.id} value={cluster.id}>
                                <Checkbox checked={(field.value || []).includes(cluster.id)} />
                                {cluster.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {(errors as any).target_clusters && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {(errors as any).target_clusters.message}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                )}

                {targetType === 'user' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth error={!!(errors as any).target_users}>
                      <InputLabel>Select Users</InputLabel>
                      <Controller
                        name="target_users"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            multiple
                            label="Select Users"
                            value={field.value || []}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(selected as string[]).map((value) => {
                                  const user = users.find((u) => u.id === value);
                                  return (
                                    <Chip
                                      key={value}
                                      label={user ? `${user.first_name} ${user.last_name}` : value}
                                      size="small"
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {users.map((user) => (
                              <MenuItem key={user.id} value={user.id}>
                                <Checkbox checked={(field.value || []).includes(user.id)} />
                                <Box>
                                  <Typography variant="body1">
                                    {user.first_name} {user.last_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {user.employee_id}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {(errors as any).target_users && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {(errors as any).target_users.message}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Create Announcement'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
  );
};

export default AnnouncementsPage;

